// ActivityFilter.tsx
import React from 'react';
import { Formik } from 'formik';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { FilterContainer } from '../Activity.styles';
import { NewSelectCircleOrPerson } from '../../../common/PickerSearch';
import { styled } from '@mui/material/styles';

const FilterComponentContainer = styled('div')({
  position: 'relative',
  borderRadius: '150px',
  flex: 1,
  minWidth: '250px',
  maxWidth: '400px',
  width: '100%',
});

const TeamFilterContainer = styled(FilterComponentContainer)({
  flex: 1.1,
  flexGrow: 1,
  paddingRight: '10px',
});

const DateFilterContainer = styled(FilterComponentContainer)({
  flex: 0.7,
  flexGrow: 1,
  paddingRight: '10px',
});

const FilterComponentTitle = styled('div')(({ theme }) => ({
  fontWeight: 700,
  fontFamily: 'Manrope',
  fontSize: '15px',
  marginBottom: '10px',
  color: theme.custom.text,
}));

const StyledDateInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '150px',
    height: '40px',
    backgroundColor: theme.custom.background1,
    color: theme.custom.text,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.custom.primary,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 14px',
    height: '20px',
    fontSize: '14px',
    color: theme.custom.text,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.custom.stroke,
  },
  '& .MuiInputBase-input': {
    colorScheme: theme.custom.isDark ? 'dark' : 'light',
  },
}));

interface ActivityFilterProps {
  selectedTeamId: string;
  selectedDate: string;
  dateError: string | null;
  handleTeamSelect: (id: string, name: string) => void;
  handleDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updatePickerKey: number;
  loading: boolean;
  onRefreshClick: () => void;
  isManager: boolean;
  allowAllTeamsOption: boolean;
}

const ActivityFilter: React.FC<ActivityFilterProps> = ({
  selectedTeamId,
  selectedDate,
  dateError,
  handleTeamSelect,
  handleDateChange,
  updatePickerKey,
  loading,
  onRefreshClick,
  isManager,
  allowAllTeamsOption,
}) => {
  return (
    <Formik
      initialValues={{ teamFilter: selectedTeamId }}
      onSubmit={() => {}}
      enableReinitialize
    >
      {() => (
        <FilterContainer>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1, md: 2 }, // Reduced gap between filters
              mb: 3,
              alignItems: 'flex-end', // Changed to align at bottom
              justifyContent: 'flex-start', // Start from left
            }}
          >
            {/* Team Filter */}
            <TeamFilterContainer>
              <FilterComponentTitle>Team</FilterComponentTitle>
              <Box
                sx={{
                  position: 'relative',
                  '& select': {
                    backgroundColor: (theme) => theme.custom.background1,
                    color: (theme) => theme.custom.text,
                    border: (theme) => `1px solid ${theme.custom.stroke}`,
                  },
                }}
              >
                <NewSelectCircleOrPerson
                  key={`team-picker-${updatePickerKey}`}
                  name="teamFilter"
                  value={selectedTeamId}
                  fieldSetter={handleTeamSelect}
                  showAll={true}
                  allOption={allowAllTeamsOption}
                  noAutoSelect={true}
                  pickCircles={true}
                  pickUsers={false}
                  useCirclesList={false}
                  useManagerCirclesList={isManager}
                  update={updatePickerKey}
                  style={{
                    height: '40px',
                    width: '200px',
                    minWidth: '200px',
                    borderRadius: '10px',
                    padding: '10px 30px 10px 10px',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    backgroundImage: 'url("/images/select-dropdown.svg")',
                    backgroundSize: '16px',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'calc(100% - 8px) center',
                    fontSize: '14px',
                  }}
                />
              </Box>
            </TeamFilterContainer>

            {/* Date Filter */}
            <DateFilterContainer>
              <FilterComponentTitle>Date</FilterComponentTitle>
              <StyledDateInput
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                error={!!dateError}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{
                  borderRadius: '150px',
                  width: '100%',
                  maxWidth: '200px',
                }}
              />
              {dateError && (
                <Typography color="error" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                  {dateError}
                </Typography>
              )}
            </DateFilterContainer>

            {/* Refresh Button */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                flex: 0.8,
                marginLeft: { xs: 0, md: 'auto' },
                marginTop: { xs: '24px', md: 0 },
                alignSelf: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: (theme) => theme.custom.primary,
                  color: (theme) => theme.custom.textCTA,
                  height: '40px',
                  '&:hover': {
                    backgroundColor: (theme) => theme.custom.primaryDark,
                  },
                }}
                onClick={onRefreshClick}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </Box>
          </Box>
        </FilterContainer>
      )}
    </Formik>
  );
};

export default ActivityFilter;
